export const GRID_MAGIC = 'LRG1'
const GRID_MAGIC_BYTES = new Uint8Array([0x4c, 0x52, 0x47, 0x31])
export const GRID_PROTOCOL_VERSION = 1
export const GRID_CHUNK_HEADER_SIZE = 32
export const GRID_SCREEN_HEADER_SIZE = 40
export const GRID_RUN_HEADER_SIZE = 15
export const GRID_FONT_HEADER_SIZE = 44
export const GRID_MAX_MESSAGE_BYTES = 16 * 1024
export const GRID_CHUNK_PAYLOAD_MAX_BYTES = GRID_MAX_MESSAGE_BYTES - 4 - GRID_CHUNK_HEADER_SIZE
export const GRID_SCREEN_MAX_BYTES = 3 * 1024 * 1024
export const GRID_FONT_MAX_BYTES = 32 * 1024 * 1024
export const GRID_FONT_SOURCE_MAX_BYTES = GRID_FONT_MAX_BYTES - GRID_FONT_HEADER_SIZE
export const GRID_IMAGE_ASSET_HEADER_SIZE = 56
export const GRID_IMAGE_ASSET_SOURCE_MAX_BYTES = 8 * 1024 * 1024
export const GRID_IMAGE_ASSET_MAX_BYTES = GRID_IMAGE_ASSET_HEADER_SIZE + GRID_IMAGE_ASSET_SOURCE_MAX_BYTES
export const GRID_IMAGE_SCENE_HEADER_SIZE = 40
export const GRID_IMAGE_PLACEMENT_BYTES = 20
export const GRID_IMAGE_MAX_PLACEMENTS = 16_384
export const GRID_IMAGE_SCENE_MAX_BYTES = GRID_IMAGE_SCENE_HEADER_SIZE +
  GRID_IMAGE_PLACEMENT_BYTES * GRID_IMAGE_MAX_PLACEMENTS
export const GRID_IMAGE_BARRIER_BYTES = 32
export const GRID_IMAGE_MAX_DIMENSION = 2048
// Wire-contract grid bounds, mirroring LERA_GRID_COLS_MIN/MAX and
// LERA_GRID_ROWS_MIN/MAX in include/lera/grid_protocol.h.
export const GRID_COLS_MIN = 20
export const GRID_COLS_MAX = 500
export const GRID_ROWS_MIN = 10
export const GRID_ROWS_MAX = 200

export enum GridChunkKind {
  Screen = 1,
  Font = 2,
  ImageAsset = 3,
  ImageScene = 4,
  ImageBarrier = 5,
}

export enum GridScreenKind {
  Full = 1,
  Patch = 2,
}

export enum GridColorType {
  Default = 0,
  Basic = 1,
  Ansi256 = 2,
  Rgb = 3,
}

export type GridProtocolErrorCode = 'decode' | 'gap'

export class GridProtocolError extends Error {
  constructor(readonly code: GridProtocolErrorCode, message: string) {
    super(message)
    this.name = 'GridProtocolError'
  }
}

export class GridImageCapacityError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'GridImageCapacityError'
  }
}

export type GridColor =
  | { type: 'default' }
  | { type: 'basic'; value: number }
  | { type: 'ansi256'; value: number }
  | { type: 'rgb'; value: [number, number, number] }

export interface DecodedGridRun {
  start: number
  count: number
  style: number
  foreground: GridColor
  background: GridColor
  chars: string[]
}

export interface DecodedScreenFrame {
  kind: 'full' | 'patch'
  sequence: number
  previousSequence: number
  configGeneration: number
  resizeGeneration: number
  cols: number
  rows: number
  cursor: { visible: boolean; col: number; row: number }
  runs: DecodedGridRun[]
}

export interface DecodedFontRecord {
  configGeneration: number
  variant: 0 | 1 | 2 | 3
  format: 1
  sha256: Uint8Array
  bytes: Uint8Array
}

export interface DecodedImageAssetRecord {
  readonly assetEpoch: number
  readonly assetId: number
  readonly width: number
  readonly height: number
  readonly format: 1
  readonly sha256: Uint8Array
  readonly bytes: Uint8Array
}

export interface DecodedImagePlacement {
  readonly assetId: number
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
  readonly fit: 'contain' | 'stretch'
  readonly filter: 'nearest' | 'linear'
}

export interface DecodedImageSceneRecord {
  readonly assetEpoch: number
  readonly sceneGeneration: number
  readonly gridSequence: number
  readonly configGeneration: number
  readonly resizeGeneration: number
  readonly cols: number
  readonly rows: number
  readonly placements: readonly DecodedImagePlacement[]
}

export interface DecodedImageBarrierRecord {
  readonly assetEpoch: number
  readonly presentationGeneration: number
  readonly gridSequence: number
  readonly sceneGeneration: number
  readonly configGeneration: number
  readonly resizeGeneration: number
}

export interface ImageAssetAllocation {
  readonly assetEpoch: number
  readonly assetId: number
  readonly width: number
  readonly height: number
  readonly format: 1
  readonly sha256: Uint8Array
  readonly encodedLength: number
  readonly logicalLength: number
}

export interface ImageAssetReservation {
  release(): void
}

export type DecodedGridRecord =
  | { kind: 'screen'; frame: DecodedScreenFrame }
  | { kind: 'font'; font: DecodedFontRecord }

export type DecodedGridStreamRecord = DecodedGridRecord
  | {
      readonly kind: 'image-asset'
      readonly asset: DecodedImageAssetRecord
      readonly reservation?: ImageAssetReservation
    }
  | { readonly kind: 'image-scene'; readonly scene: DecodedImageSceneRecord }
  | { readonly kind: 'image-barrier'; readonly barrier: DecodedImageBarrierRecord }

class ByteReader {
  private offset = 0

  constructor(private readonly bytes: Uint8Array) {}

  get remaining(): number {
    return this.bytes.length - this.offset
  }

  u8(): number {
    this.require(1)
    return this.bytes[this.offset++]
  }

  u16(): number {
    this.require(2)
    const value = (this.bytes[this.offset] << 8) | this.bytes[this.offset + 1]
    this.offset += 2
    return value
  }

  u32(): number {
    this.require(4)
    const value = ((this.bytes[this.offset] * 0x1000000) +
      (this.bytes[this.offset + 1] << 16) +
      (this.bytes[this.offset + 2] << 8) + this.bytes[this.offset + 3])
    this.offset += 4
    return value
  }

  bytesOf(length: number): Uint8Array {
    return this.viewOf(length).slice()
  }

  viewOf(length: number): Uint8Array {
    this.require(length)
    const value = this.bytes.subarray(this.offset, this.offset + length)
    this.offset += length
    return value
  }

  zeroes(length: number, label: string): void {
    const value = this.bytesOf(length)
    if (value.some((byte) => byte !== 0)) fail(`${label} must be zero`)
  }

  done(): void {
    if (this.remaining !== 0) fail('trailing bytes')
  }

  private require(length: number): void {
    if (!Number.isSafeInteger(length) || length < 0 || this.remaining < length) {
      fail('truncated record')
    }
  }
}

interface ChunkHeader {
  kind: GridChunkKind
  transactionId: number
  index: number
  count: number
  total: number
  offset: number
  payload: Uint8Array
}

interface PendingRecord {
  transactionId: number
  count: number
  total: number
  nextIndex: number
  nextOffset: number
  bytes: Uint8Array
  imageAssetReservation?: ImageAssetReservation
}

function fail(message: string, code: GridProtocolErrorCode = 'decode'): never {
  throw new GridProtocolError(code, message)
}

function checkMagic(bytes: Uint8Array): void {
  for (let index = 0; index < GRID_MAGIC_BYTES.length; index++) {
    if (bytes[index] !== GRID_MAGIC_BYTES[index]) fail('bad grid magic')
  }
}

function envelopeTransactionId(message: Uint8Array): number | undefined {
  if (message.length < 12 || message[4] !== GRID_PROTOCOL_VERSION) return undefined
  for (let index = 0; index < GRID_MAGIC_BYTES.length; index++) {
    if (message[index] !== GRID_MAGIC_BYTES[index]) return undefined
  }
  return (message[8] * 0x1000000) + (message[9] << 16) + (message[10] << 8) + message[11]
}

function isImageKind(kind: GridChunkKind): boolean {
  return kind === GridChunkKind.ImageAsset || kind === GridChunkKind.ImageScene ||
    kind === GridChunkKind.ImageBarrier
}

function maxForKind(kind: GridChunkKind): number {
  switch (kind) {
    case GridChunkKind.Screen: return GRID_SCREEN_MAX_BYTES
    case GridChunkKind.Font: return GRID_FONT_MAX_BYTES
    case GridChunkKind.ImageAsset: return GRID_IMAGE_ASSET_MAX_BYTES
    case GridChunkKind.ImageScene: return GRID_IMAGE_SCENE_MAX_BYTES
    case GridChunkKind.ImageBarrier: return GRID_IMAGE_BARRIER_BYTES
  }
}

function decodeHeader(message: Uint8Array, imageV1: boolean): ChunkHeader {
  if (message.length > GRID_MAX_MESSAGE_BYTES) fail('grid message exceeds maximum size')
  if (message.length < GRID_CHUNK_HEADER_SIZE) fail('truncated chunk header')
  checkMagic(message)
  const reader = new ByteReader(message)
  reader.bytesOf(4)
  if (reader.u8() !== GRID_PROTOCOL_VERSION) fail('unsupported grid protocol version')
  const kindValue = reader.u8()
  if (kindValue < GridChunkKind.Screen || kindValue > GridChunkKind.ImageBarrier) fail('invalid grid chunk kind')
  const kind = kindValue as GridChunkKind
  if (isImageKind(kind) && !imageV1) fail('image-v1 chunk received before feature acknowledgement')
  if (reader.u16() !== GRID_CHUNK_HEADER_SIZE) fail('invalid grid chunk header size')
  const transactionId = reader.u32()
  if (transactionId === 0) fail('zero transaction id')
  const index = reader.u16()
  const count = reader.u16()
  if (count === 0 || index >= count) fail('invalid chunk range')
  const total = reader.u32()
  const offset = reader.u32()
  const payloadLength = reader.u32()
  reader.zeroes(4, 'chunk reserved field')
  if (payloadLength !== reader.remaining) fail('declared chunk payload length does not match message')
  if (total === 0) fail('empty logical record')
  if (total > maxForKind(kind)) fail('oversized logical record')
  if (offset > total || payloadLength > total - offset) fail('chunk exceeds logical record')
  if (isImageKind(kind)) {
    const expectedCount = Math.ceil(total / GRID_CHUNK_PAYLOAD_MAX_BYTES)
    const expectedOffset = index * GRID_CHUNK_PAYLOAD_MAX_BYTES
    const expectedPayload = Math.min(GRID_CHUNK_PAYLOAD_MAX_BYTES, total - expectedOffset)
    if (payloadLength === 0 || payloadLength > GRID_CHUNK_PAYLOAD_MAX_BYTES ||
        count !== expectedCount || offset !== expectedOffset || offset >= total ||
        payloadLength !== expectedPayload) fail('non-canonical image chunk envelope')
  }
  return { kind, transactionId, index, count, total, offset, payload: reader.bytesOf(payloadLength) }
}

function decodeColor(reader: ByteReader): GridColor {
  const type = reader.u8()
  const value = reader.bytesOf(3)
  switch (type) {
    case GridColorType.Default:
      if (value.some((byte) => byte !== 0)) fail('default color has a value')
      return { type: 'default' }
    case GridColorType.Basic:
      if (value[1] !== 0 || value[2] !== 0) fail('indexed color has non-zero trailing bytes')
      if (value[0] > 15) fail('basic color out of bounds')
      return { type: 'basic', value: value[0] }
    case GridColorType.Ansi256:
      if (value[1] !== 0 || value[2] !== 0) fail('indexed color has non-zero trailing bytes')
      return { type: 'ansi256', value: value[0] }
    case GridColorType.Rgb:
      return { type: 'rgb', value: [value[0], value[1], value[2]] }
    default:
      return fail('invalid color type')
  }
}

function decodeScalar(length: number, raw: Uint8Array): string {
  if (length === 0) {
    if (raw.some((byte) => byte !== 0)) fail('empty character has non-zero bytes')
    return ''
  }
  if (length > 4 || raw.slice(length).some((byte) => byte !== 0)) fail('invalid UTF-8 length')
  const bytes = raw.slice(0, length)
  let scalar: number
  if (length === 1) {
    if (bytes[0] > 0x7f) fail('invalid UTF-8 leading byte')
    scalar = bytes[0]
  } else {
    const masks = [0, 0, 0x1f, 0x0f, 0x07]
    const minimums = [0, 0, 0x80, 0x800, 0x10000]
    const first = bytes[0]
    if ((length === 2 && (first < 0xc2 || first > 0xdf)) ||
        (length === 3 && (first < 0xe0 || first > 0xef)) ||
        (length === 4 && (first < 0xf0 || first > 0xf4))) fail('invalid UTF-8 leading byte')
    scalar = first & masks[length]
    for (let index = 1; index < length; index++) {
      const continuation = bytes[index]
      if ((continuation & 0xc0) !== 0x80) fail('invalid UTF-8 continuation')
      scalar = (scalar << 6) | (continuation & 0x3f)
    }
    if (scalar < minimums[length]) fail('overlong UTF-8 scalar')
  }
  if (scalar >= 0xd800 && scalar <= 0xdfff) fail('UTF-8 surrogate scalar')
  if (scalar > 0x10ffff) fail('UTF-8 scalar exceeds Unicode range')
  return String.fromCodePoint(scalar)
}

function decodeScreen(bytes: Uint8Array): DecodedScreenFrame {
  const reader = new ByteReader(bytes)
  const kindValue = reader.u8()
  if (kindValue !== GridScreenKind.Full && kindValue !== GridScreenKind.Patch) fail('invalid screen frame kind')
  const cursorVisible = reader.u8()
  if (cursorVisible !== 0 && cursorVisible !== 1) fail('invalid cursor visibility')
  if (reader.u16() !== GRID_SCREEN_HEADER_SIZE) fail('invalid screen header size')
  const sequence = reader.u32()
  const previousSequence = reader.u32()
  if ((kindValue === GridScreenKind.Full && previousSequence !== 0) ||
      (kindValue === GridScreenKind.Patch && previousSequence === 0)) fail('invalid previous sequence')
  const configGeneration = reader.u32()
  const resizeGeneration = reader.u32()
  const cols = reader.u16()
  const rows = reader.u16()
  if (cols < GRID_COLS_MIN || cols > GRID_COLS_MAX ||
      rows < GRID_ROWS_MIN || rows > GRID_ROWS_MAX) fail('screen dimensions out of bounds')
  const cursor = { visible: cursorVisible === 1, col: reader.u16(), row: reader.u16() }
  if (cursor.col >= cols || cursor.row >= rows) fail('cursor out of bounds')
  const runCount = reader.u32()
  reader.zeroes(8, 'screen reserved field')
  const cells = cols * rows
  const covered = kindValue === GridScreenKind.Full ? new Uint8Array(cells) : undefined
  const runs: DecodedGridRun[] = []
  let previousRunEnd = 0
  for (let index = 0; index < runCount; index++) {
    const start = reader.u32()
    const count = reader.u16()
    if (count === 0 || start > cells || count > cells - start) fail('run out of bounds')
    if (start < previousRunEnd) fail('screen runs overlap or are unordered')
    if (covered !== undefined) {
      for (let cell = start; cell < start + count; cell++) {
        if (covered[cell] !== 0) fail('full frame runs overlap')
        covered[cell] = 1
      }
    }
    const style = reader.u8()
    const foreground = decodeColor(reader)
    const background = decodeColor(reader)
    const chars: string[] = []
    for (let character = 0; character < count; character++) {
      chars.push(decodeScalar(reader.u8(), reader.bytesOf(4)))
    }
    previousRunEnd = start + count
    runs.push({ start, count, style, foreground, background, chars })
  }
  reader.done()
  if (covered !== undefined && covered.some((cell) => cell === 0)) fail('full frame does not cover every cell')
  return {
    kind: kindValue === GridScreenKind.Full ? 'full' : 'patch',
    sequence, previousSequence, configGeneration, resizeGeneration, cols, rows, cursor, runs,
  }
}

function decodeFont(bytes: Uint8Array): DecodedFontRecord {
  const reader = new ByteReader(bytes)
  const configGeneration = reader.u32()
  const variant = reader.u8()
  if (variant > 3) fail('invalid font variant')
  if (reader.u8() !== 1) fail('invalid font format')
  reader.zeroes(2, 'font reserved field')
  const sha256 = reader.bytesOf(32)
  const length = reader.u32()
  if (length > GRID_FONT_SOURCE_MAX_BYTES || length !== reader.remaining) fail('invalid font byte length')
  return { configGeneration, variant: variant as 0 | 1 | 2 | 3, format: 1, sha256, bytes: reader.viewOf(length) }
}

function nonzero(value: number, label: string): number {
  if (value === 0) fail(`${label} must be non-zero`)
  return value
}

function decodeImageAssetHeader(bytes: Uint8Array, logicalLength: number): ImageAssetAllocation {
  if (bytes.length < GRID_IMAGE_ASSET_HEADER_SIZE) fail('truncated image asset header')
  const reader = new ByteReader(bytes.subarray(0, GRID_IMAGE_ASSET_HEADER_SIZE))
  if (reader.u8() !== 1) fail('unsupported image asset version')
  if (reader.u8() !== 1) fail('invalid image asset format')
  if (reader.u16() !== GRID_IMAGE_ASSET_HEADER_SIZE) fail('invalid image asset header size')
  const assetEpoch = nonzero(reader.u32(), 'asset epoch')
  const assetId = nonzero(reader.u32(), 'asset ID')
  const width = reader.u16()
  const height = reader.u16()
  if (width < 1 || width > GRID_IMAGE_MAX_DIMENSION ||
      height < 1 || height > GRID_IMAGE_MAX_DIMENSION) fail('image asset dimensions out of bounds')
  const encodedLength = reader.u32()
  if (encodedLength === 0 || encodedLength > GRID_IMAGE_ASSET_SOURCE_MAX_BYTES ||
      logicalLength !== GRID_IMAGE_ASSET_HEADER_SIZE + encodedLength) fail('invalid encoded image byte length')
  const sha256 = reader.bytesOf(32)
  reader.zeroes(4, 'image asset reserved field')
  return { assetEpoch, assetId, width, height, format: 1, sha256, encodedLength, logicalLength }
}

function decodeImageAsset(bytes: Uint8Array): DecodedImageAssetRecord {
  const allocation = decodeImageAssetHeader(bytes, bytes.length)
  const reader = new ByteReader(bytes)
  reader.bytesOf(20)
  const sha256 = reader.bytesOf(32)
  reader.zeroes(4, 'image asset reserved field')
  const encoded = reader.bytesOf(allocation.encodedLength)
  reader.done()
  return Object.freeze({
    assetEpoch: allocation.assetEpoch,
    assetId: allocation.assetId,
    width: allocation.width,
    height: allocation.height,
    format: 1 as const,
    sha256,
    bytes: encoded,
  })
}

interface DecodedImageSceneHeader {
  readonly assetEpoch: number
  readonly sceneGeneration: number
  readonly gridSequence: number
  readonly configGeneration: number
  readonly resizeGeneration: number
  readonly cols: number
  readonly rows: number
  readonly count: number
}

function decodeImageSceneHeader(bytes: Uint8Array, logicalLength: number): DecodedImageSceneHeader {
  if (bytes.length < GRID_IMAGE_SCENE_HEADER_SIZE) fail('truncated image scene header')
  const reader = new ByteReader(bytes.subarray(0, GRID_IMAGE_SCENE_HEADER_SIZE))
  if (reader.u8() !== 1) fail('unsupported image scene version')
  if (reader.u8() !== 1) fail('invalid image scene kind')
  if (reader.u16() !== GRID_IMAGE_SCENE_HEADER_SIZE) fail('invalid image scene header size')
  const assetEpoch = nonzero(reader.u32(), 'asset epoch')
  const sceneGeneration = nonzero(reader.u32(), 'scene generation')
  const gridSequence = nonzero(reader.u32(), 'grid sequence')
  const configGeneration = nonzero(reader.u32(), 'configuration generation')
  const resizeGeneration = nonzero(reader.u32(), 'resize generation')
  const cols = reader.u16()
  const rows = reader.u16()
  if (cols < GRID_COLS_MIN || cols > GRID_COLS_MAX ||
      rows < GRID_ROWS_MIN || rows > GRID_ROWS_MAX) fail('image scene dimensions out of bounds')
  const count = reader.u32()
  if (count > GRID_IMAGE_MAX_PLACEMENTS) fail('too many image placements')
  if (reader.u16() !== GRID_IMAGE_PLACEMENT_BYTES) fail('invalid image placement record size')
  reader.zeroes(6, 'image scene reserved field')
  const placementBytes = count * GRID_IMAGE_PLACEMENT_BYTES
  if (!Number.isSafeInteger(placementBytes) ||
      logicalLength !== GRID_IMAGE_SCENE_HEADER_SIZE + placementBytes) {
    fail('image scene placement length mismatch')
  }
  reader.done()
  return {
    assetEpoch, sceneGeneration, gridSequence, configGeneration, resizeGeneration,
    cols, rows, count,
  }
}

function decodeImageScene(bytes: Uint8Array): DecodedImageSceneRecord {
  const header = decodeImageSceneHeader(bytes, bytes.length)
  const reader = new ByteReader(bytes)
  reader.bytesOf(GRID_IMAGE_SCENE_HEADER_SIZE)
  const placements: DecodedImagePlacement[] = []
  for (let index = 0; index < header.count; index++) {
    const assetId = nonzero(reader.u32(), 'placement asset ID')
    const x = reader.u16()
    const y = reader.u16()
    const width = reader.u16()
    const height = reader.u16()
    if (x >= header.cols || y >= header.rows || width === 0 || height === 0) {
      fail('invalid image placement bounds')
    }
    const right = x + width
    const bottom = y + height
    if (!Number.isSafeInteger(right) || !Number.isSafeInteger(bottom)) fail('image placement overflow')
    const fitValue = reader.u8()
    const filterValue = reader.u8()
    if (fitValue !== 1 && fitValue !== 2) fail('invalid image placement fit')
    if (filterValue !== 1 && filterValue !== 2) fail('invalid image placement filter')
    reader.zeroes(6, 'image placement reserved field')
    placements.push(Object.freeze({
      assetId, x, y, width, height,
      fit: fitValue === 1 ? 'contain' : 'stretch',
      filter: filterValue === 1 ? 'nearest' : 'linear',
    }))
  }
  reader.done()
  return Object.freeze({
    assetEpoch: header.assetEpoch,
    sceneGeneration: header.sceneGeneration,
    gridSequence: header.gridSequence,
    configGeneration: header.configGeneration,
    resizeGeneration: header.resizeGeneration,
    cols: header.cols,
    rows: header.rows,
    placements: Object.freeze(placements),
  })
}

function decodeImageBarrierHeader(bytes: Uint8Array, logicalLength: number): DecodedImageBarrierRecord {
  if (bytes.length < GRID_IMAGE_BARRIER_BYTES || logicalLength !== GRID_IMAGE_BARRIER_BYTES) {
    fail('invalid image barrier size')
  }
  const reader = new ByteReader(bytes.subarray(0, GRID_IMAGE_BARRIER_BYTES))
  if (reader.u8() !== 1) fail('unsupported image barrier version')
  if (reader.u8() !== 0) fail('invalid image barrier flags')
  if (reader.u16() !== GRID_IMAGE_BARRIER_BYTES) {
    fail('invalid image barrier size')
  }
  const value = Object.freeze({
    assetEpoch: nonzero(reader.u32(), 'asset epoch'),
    presentationGeneration: nonzero(reader.u32(), 'presentation generation'),
    gridSequence: nonzero(reader.u32(), 'grid sequence'),
    sceneGeneration: nonzero(reader.u32(), 'scene generation'),
    configGeneration: nonzero(reader.u32(), 'configuration generation'),
    resizeGeneration: nonzero(reader.u32(), 'resize generation'),
  })
  reader.zeroes(4, 'image barrier reserved field')
  reader.done()
  return value
}

function decodeImageBarrier(bytes: Uint8Array): DecodedImageBarrierRecord {
  return decodeImageBarrierHeader(bytes, bytes.length)
}

export interface GridChunkAssemblerOptions<WithImages extends boolean = false> {
  readonly imageV1?: WithImages
  readonly canAllocateImageAsset?: (allocation: ImageAssetAllocation) => boolean
  readonly reserveImageAsset?: (allocation: ImageAssetAllocation) => ImageAssetReservation | null
  readonly activeImageEpoch?: () => number
}

export class GridChunkAssembler<WithImages extends boolean = false> {
  private readonly pending = new Map<GridChunkKind, PendingRecord>()
  private transactionHighWater = 0
  private lastCompletedTransactionId = 0
  private imageV1: boolean
  private readonly canAllocateImageAsset?: (allocation: ImageAssetAllocation) => boolean
  private readonly reserveImageAsset?: (allocation: ImageAssetAllocation) => ImageAssetReservation | null
  private readonly activeImageEpoch?: () => number

  constructor(options: GridChunkAssemblerOptions<WithImages> = {}) {
    this.imageV1 = options.imageV1 ?? false
    this.canAllocateImageAsset = options.canAllocateImageAsset
    this.reserveImageAsset = options.reserveImageAsset
    this.activeImageEpoch = options.activeImageEpoch
  }

  enableImageV1(): void {
    this.imageV1 = true
  }

  resetForRecovery(): void {
    this.clearPending()
    this.transactionHighWater = this.lastCompletedTransactionId
  }

  private clearPending(): void {
    for (const state of this.pending.values()) this.releaseReservation(state)
    this.pending.clear()
  }

  private consumeTransaction(transactionId: number | undefined): void {
    if (transactionId === undefined || transactionId === 0) return
    if (transactionId > this.transactionHighWater) this.discardPendingBefore(transactionId)
    for (const [kind, state] of this.pending) {
      if (state.transactionId === transactionId) {
        this.releaseReservation(state)
        this.pending.delete(kind)
      }
    }
    this.transactionHighWater = Math.max(this.transactionHighWater, transactionId)
  }

  private discardPendingBefore(transactionId: number): void {
    for (const [kind, state] of this.pending) {
      if (state.transactionId < transactionId) {
        this.releaseReservation(state)
        this.pending.delete(kind)
      }
    }
  }

  private releaseReservation(state: PendingRecord): void {
    state.imageAssetReservation?.release()
    state.imageAssetReservation = undefined
  }

  push(message: ArrayBuffer | Uint8Array):
      (WithImages extends true ? DecodedGridStreamRecord : DecodedGridRecord) | null {
    const bytes = message instanceof Uint8Array ? message : new Uint8Array(message)
    const transactionId = envelopeTransactionId(bytes)
    try {
      const header = decodeHeader(bytes, this.imageV1)
      let state: PendingRecord
      if (header.transactionId > this.transactionHighWater) {
        if (header.index !== 0 || header.offset !== 0) fail('new transaction does not begin at first chunk', 'gap')
        this.discardPendingBefore(header.transactionId)
        let imageAssetReservation: ImageAssetReservation | undefined
        let imageEpoch: number | undefined
        let allocation: ImageAssetAllocation | undefined
        switch (header.kind) {
          case GridChunkKind.ImageAsset:
            allocation = decodeImageAssetHeader(header.payload, header.total)
            imageEpoch = allocation.assetEpoch
            break
          case GridChunkKind.ImageScene:
            imageEpoch = decodeImageSceneHeader(header.payload, header.total).assetEpoch
            break
          case GridChunkKind.ImageBarrier:
            imageEpoch = decodeImageBarrierHeader(header.payload, header.total).assetEpoch
            break
        }
        if (imageEpoch !== undefined && this.activeImageEpoch !== undefined &&
            imageEpoch !== this.activeImageEpoch()) {
          fail('image record epoch does not match the active epoch')
        }
        if (allocation !== undefined) {
          if (this.reserveImageAsset !== undefined) {
            imageAssetReservation = this.reserveImageAsset(allocation) ?? undefined
            if (imageAssetReservation === undefined) {
              throw new GridImageCapacityError('active image asset capacity exceeded')
            }
          } else if (this.canAllocateImageAsset !== undefined && !this.canAllocateImageAsset(allocation)) {
            throw new GridImageCapacityError('active image asset capacity exceeded')
          }
        }
        let recordBytes: Uint8Array
        try {
          recordBytes = new Uint8Array(header.total)
        } catch (error) {
          imageAssetReservation?.release()
          throw error
        }
        state = {
          transactionId: header.transactionId,
          count: header.count,
          total: header.total,
          nextIndex: 0,
          nextOffset: 0,
          bytes: recordBytes,
          imageAssetReservation,
        }
        this.pending.set(header.kind, state)
        this.transactionHighWater = header.transactionId
      } else {
        if (header.transactionId < this.transactionHighWater) fail('older transaction', 'gap')
        const prior = this.pending.get(header.kind)
        if (prior === undefined || prior.transactionId !== header.transactionId) fail('older or duplicate transaction', 'gap')
        state = prior
        if (header.count !== state.count || header.total !== state.total) fail('inconsistent transaction envelope', 'gap')
      }
      if (header.index !== state.nextIndex || header.offset !== state.nextOffset) fail('duplicate or gapped chunk', 'gap')
      state.bytes.set(header.payload, header.offset)
      state.nextIndex++
      state.nextOffset += header.payload.length
      if (state.nextIndex !== state.count) return null
      if (state.nextOffset !== state.total) fail('truncated logical record', 'gap')
      this.pending.delete(header.kind)
      let record: DecodedGridStreamRecord
      try {
        switch (header.kind) {
          case GridChunkKind.Screen:
            record = { kind: 'screen', frame: decodeScreen(state.bytes) }
            break
          case GridChunkKind.Font:
            record = { kind: 'font', font: decodeFont(state.bytes) }
            break
          case GridChunkKind.ImageAsset:
            record = Object.freeze({
              kind: 'image-asset',
              asset: decodeImageAsset(state.bytes),
              ...(state.imageAssetReservation === undefined
                ? {}
                : { reservation: state.imageAssetReservation }),
            })
            state.imageAssetReservation = undefined
            break
          case GridChunkKind.ImageScene:
            record = Object.freeze({ kind: 'image-scene', scene: decodeImageScene(state.bytes) })
            break
          case GridChunkKind.ImageBarrier:
            record = Object.freeze({ kind: 'image-barrier', barrier: decodeImageBarrier(state.bytes) })
            break
        }
      } catch (error) {
        this.releaseReservation(state)
        throw error
      }
      this.lastCompletedTransactionId = header.transactionId
      return record as WithImages extends true ? DecodedGridStreamRecord : DecodedGridRecord
    } catch (error) {
      if (transactionId === undefined) this.clearPending()
      else this.consumeTransaction(transactionId)
      throw error
    }
  }
}
